import {
  FormControl,
  TextField as MuiTextField,
  type TextFieldProps as MuiTextFieldProps,
  OutlinedInput,
  type OutlinedInputProps,
  styled,
} from '@mui/material';
import type { UseFormRegisterReturn } from 'react-hook-form';
import { RequiredMarker } from './RequiredMarker';

const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== 'inline',
})<{ inline?: boolean }>(({ theme, inline }) => ({
  flexDirection: inline ? 'row' : 'column',
  alignItems: inline ? 'center' : 'default',
  gap: inline ? theme.spacing(2) : theme.spacing(1),
}));

const StyleTitleContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

type TextFieldProps = MuiTextFieldProps & {
  inline?: boolean;
  boldLabel?: boolean;
  labelIcon?: React.ReactNode;
  register?: UseFormRegisterReturn<string>;
};

export const TextField = (props: TextFieldProps & OutlinedInputProps) => {
  const {
    inline,
    label,
    register,
    required,
    boldLabel,
    labelIcon,
    fullWidth,
    helperText,
    ...muiProps
  } = props;

  return (
    <StyledFormControl inline={inline} size="small" fullWidth={fullWidth ?? true}>
      <StyleTitleContainer>
        {labelIcon}
        {boldLabel ? (
          <span className="text-sm leading-normal uppercase font-semibold text-text-neutral-secondary">
            {props.label}
            {required && <RequiredMarker />}
          </span>
        ) : (
          <span className="text-sm leading-normal">
            {props.label}
            {required && <RequiredMarker />}
          </span>
        )}
      </StyleTitleContainer>
      {props.startAdornment || props.endAdornment ? (
        <OutlinedInput size="small" required={required} {...muiProps} {...register} />
      ) : (
        <MuiTextField
          variant="outlined"
          helperText={helperText}
          required={required}
          {...muiProps}
          size="small"
          {...register}
        />
      )}
    </StyledFormControl>
  );
};
